import React, {useMemo} from "react";
import {Tooltip} from "@mui/material";
import {useAsync2} from "nate-react-api-helpers";
import {grey} from "@mui/material/colors";
import {api} from "../../../../../api/API";
import {HardwareGroupWithContent, HardwareItem} from "../../../../../api/Hardware";
import {Loader} from "../../../../../misc/Loader";
import {useProjectId} from "../../ProjectName";
import {ChangePreviewContext} from "../../ShopDrawingChangePreview";
import {cellInActive} from "../../pricing/cellconst";
import {colCount} from "../HardwareTable";
import {tableClass} from "../library/ShortCodes";
import {IconCell} from "../library/IconCell";
import {ProductDimensioned} from "../library/ProductDimensioned";
import {StatusCell} from "../StatusCell";
import {InfoPopout} from "../InfoPopout";
import {decodeHanding} from "../../../quote/openings/QuoteOpenings";
import {qtyWidth} from "../WorkingHardware";
import {getProductCode} from "../HardwareGroup";

export function HardwareGroupView(props: {
    group: number;
    changeset?: number
}) {
    const project = useProjectId();
    const {group, changeset} = props;

    const data = useAsync2(
        (input) => api.hardware.listWithContents(input),
        {project, groups: [group], previewChangeset: changeset},
        [project, group, changeset]
    );

    const previewCtx = useMemo(() => ({
        enabled: !!changeset,
        sessionId: changeset ?? 0,
    }), [changeset]);

    return (
        <ChangePreviewContext.Provider value={previewCtx}>
            <Loader {...data}>
                {(list: HardwareGroupWithContent[]) => {
                    const value = list[0];
                    if (!value) {
                        return (
                            <div style={{padding: 16, color: grey["700"]}}>
                                Hardware group not available at this point in the timeline.
                            </div>
                        );
                    }

                    return <HardwareGroupViewInner value={value} proposing={!!changeset} />;
                }}
            </Loader>
        </ChangePreviewContext.Provider>
    );
}

function HardwareGroupViewInner(props: {
    value: HardwareGroupWithContent;
    proposing: boolean;
}) {
    const {value, proposing} = props;

    const greyed = proposing && (!value.backup || value.name === value.backup?.name);

    const info = useMemo(() => {
        if (value.openings.length === 0) return null;
        const op = value.openings[0];
        const handing = decodeHanding(op.handing || "", value.flipDoubleActiveHanding);

        return {
            activeHanding: handing.active,
            inActiveHanding: handing.inActive,
            bothActive: handing.doubleActive,
        };
    }, [value]);

    const visibleHardware = value.hardware.filter(h => h.commonQty > 0 || h.activeQty > 0 || h.inactiveQty > 0);

    return (
        <div style={{backgroundColor: cellInActive, padding: 0}}>
            <table className={tableClass}>
                <thead>
                    <tr>
                        <th />
                        <th style={{width: 100}} colSpan={2}>Opening</th>
                        <th style={{width: 170}}>Code</th>
                        <th style={{width: 100}}>Finish</th>
                        <th style={{width: 100}}>Dimensions</th>
                        <th style={{width: qtyWidth}}>
                            <Tooltip title="Active Quantity"><div>A.Qty</div></Tooltip>
                        </th>
                        <th style={{width: qtyWidth}}>
                            <Tooltip title="Inactive Door Quantity"><div>I.Qty</div></Tooltip>
                        </th>
                        <th style={{width: qtyWidth}}>
                            <Tooltip title="Common Quantity (thresholds, seals etc)"><div>C.Qty</div></Tooltip>
                        </th>
                        <th />
                        <th style={{width: "auto"}}>Note</th>
                    </tr>
                </thead>
                <tbody>
                    <tr key="group-name">
                        <td style={{backgroundColor: greyed ? cellInActive : undefined}} />
                        <td colSpan={colCount - 1} style={{
                            fontWeight: 500,
                            backgroundColor: greyed ? cellInActive : undefined,
                        }}>
                            Heading {value.name || ""}{value.prepConfigIsCustom ? " (Custom Locations)" : null}
                        </td>
                    </tr>

                    {value.openings.map(o => (
                        <tr key={"opening-" + o.id} style={{
                            backgroundColor: proposing && !o.sessionChanged ? cellInActive : undefined,
                        }}>
                            <td />
                            <td colSpan={colCount - 1} style={{fontWeight: 500}}>
                                {o.descriptiveName} {o.doorName} -- {o.handing}
                            </td>
                        </tr>
                    ))}

                    {info?.bothActive && (
                        <tr key="group-header">
                            <td colSpan={6} />
                            <td style={{textAlign: "right"}}>
                                <div style={{display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
                                    <InfoPopout value={'This group will use special "Both Active" product rule for preps'} />
                                    <div style={{width: 3}} />
                                    {info.activeHanding}
                                </div>
                            </td>
                            <td style={{textAlign: "right"}}>{info.inActiveHanding}</td>
                            <td />
                            <td />
                        </tr>
                    )}

                    {visibleHardware.map((h, index) => {
                        const rowBg = greyed && (!h.backup || h.backup?.archived === false)
                            ? cellInActive
                            : undefined;

                        return (
                            <tr key={"hardware-" + index} style={{
                                backgroundColor: rowBg,
                                opacity: h.customerSupplied ? 0.5 : undefined,
                            }}>
                                <IconCell>{null}</IconCell>
                                <td />
                                <td>
                                    <div style={{display: "flex", alignItems: "center", paddingBottom: 2}}>
                                        <span>{h.name}{h.isCustom ? " (Custom)" : null}</span>
                                    </div>
                                </td>
                                <td style={{backgroundColor: checkBg(h, "productCode", greyed)}}>
                                    {getProductCode(h)}
                                </td>
                                <td style={{backgroundColor: checkBg(h, "finish", greyed)}}>{h.finish}</td>
                                <td>
                                    <ProductDimensioned id={h.product} show={!!h.dimensionFormula} dimensions={h.calcByDimension} />
                                </td>
                                {h.productIsAutoQtyHinge ? (
                                    <>
                                        <td style={{textAlign: "right"}}>{renderHingeQty(h.hingeActiveQtys, h.overrideHingeActiveQty)}</td>
                                        <td style={{textAlign: "right"}}>{renderHingeQty(h.hingeInactiveQtys, h.overrideHingeInActiveQty)}</td>
                                        <td style={{textAlign: "right"}}>-</td>
                                    </>
                                ) : (
                                    <>
                                        <td style={{textAlign: "right", backgroundColor: checkBg(h, "activeQty", greyed)}}>{h.activeQty}</td>
                                        <td style={{textAlign: "right", backgroundColor: checkBg(h, "inactiveQty", greyed)}}>{h.inactiveQty}</td>
                                        <td style={{textAlign: "right", backgroundColor: checkBg(h, "commonQty", greyed)}}>{h.commonQty}</td>
                                    </>
                                )}
                                <StatusCell value={h} />
                                <td style={{backgroundColor: checkBg(h, "note", greyed)}}>{h.note}</td>
                            </tr>
                        );
                    })}

                </tbody>
            </table>
        </div>
    );
}

function checkBg(h: HardwareItem, key: keyof Omit<HardwareItem, "backup">, greyed: boolean): string | undefined {
    if (!h.backup) return undefined;
    return greyed && h.backup && h[key] !== h.backup?.[key] ? "white" : undefined;
}

function renderHingeQty(qtys: number[] | undefined, override: number | null): React.ReactNode {
    if (override !== null && override !== undefined) return override;
    if (!qtys || qtys.length === 0) return "-";
    return qtys.join(", ");
}
